import React, { useState } from 'react';
import { useInputHelper, InputListTemplateProps } from 'framework/form';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useFieldHelper } from 'framework/ui/field';
import { SkillsInputsConfig } from '../../types';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { buildDefaultProperties } from '../../employeeSkillHelpers';
import EmployeeSkillsDropdown from '../inputs/EmployeeSkillsDropdown/EmployeeSkillsDropdown';
import SkillInputConfig from '../inputs/InputTemplatesByFyeldType/SkillInputConfig';
import { Box, Collapse } from '@mui/material';
import { SKILL_STATUS } from '../../constants';

type SkillFormProps = {
  propertiesSource: string;
  currentSkill: any;
};

const InputsBySkillForm = ({ propertiesSource, currentSkill }: SkillFormProps) => {
  const { getValue } = useFieldHelper<SkillsInputsConfig>({ source: 'skillsShema' });
  const skillsShema = getValue();

  //  Get Inputs config by Skill
  const skillPropertiesShema = skillsShema.skills.find(item => item.skillName === currentSkill);

  if (!skillPropertiesShema?.properties) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="10px"
      p="10px 0px 0px 25px"
      sx={{
        '> .MuiFormControl-root': {
          mb: '0px !important',
        },
        '.form-control': {
          maxWidth: '300px',
        },
        '.floating-label-dropdown': {
          mb: '0px !important',
          maxWidth: '300px',
        },
      }}
    >
      <SkillInputConfig propertiesSource={propertiesSource} propertiesShema={skillPropertiesShema.properties} />
    </Box>
  );
};

const SkillsTemplate = ({ ...props }: InputListTemplateProps) => {
  const { getSource, getValue: getCurrentSkillValue } = useInputHelper(props);

  const source = getSource() || '';

  const { getValue: getSkillsShema } = useFieldHelper<SkillsInputsConfig>({ source: 'skillsShema' });
  const { getRawOnChangeCallback } = useInputHelper({ source: `${source}.properties` });

  const { skillName, properties = {} } = getCurrentSkillValue() || {};

  const [isOpen, setIsOpen] = useState(!skillName);
  const rawOnChangeCallback = getRawOnChangeCallback();
  const skillsShema = getSkillsShema();

  const onSkillChangeCallback = (values: FormFieldsData) => {
    const skillNameValue = Object.values(values)[0];

    //  Get Inputs config by Skill
    const skillPropertiesShema = skillsShema.skills.find(item => item.skillName === skillNameValue);
    const defaultValues = buildDefaultProperties(skillPropertiesShema?.properties || []);
    rawOnChangeCallback!({
      [source]: {
        skillName: skillNameValue,
        status: SKILL_STATUS.TRAINING,
        properties: defaultValues,
      },
    });
  };

  const toggle = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <Box flexBasis="94%">
      <Box
        display="flex"
        justifyContent="space-between"
        width="48%"
        sx={{
          '> .floating-label-dropdown': {
            mb: '0px !important',
          },
        }}
      >
        <EmployeeSkillsDropdown onChangeCallback={onSkillChangeCallback} source={`${source}.skillName`} />
      </Box>

      <Box>
        <Collapse in={isOpen} timeout="auto">
          <InputsBySkillForm currentSkill={skillName} propertiesSource={`${source}.properties`} />
        </Collapse>

        {Object.keys(properties).length ? (
          <Box
            onClick={toggle}
            sx={{
              position: 'relative',
              '::before': {
                content: '""',
                position: 'absolute',
                top: '57%',
                left: '20px',
                right: 0,
                height: '0px',
                borderBottom: '1px solid var(--mui-palette-TableCell-border)',
                zIndex: 0,
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: 0,
                width: '20px',
                height: '20px',
                transition: 'transform 0.3s ease',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <KeyboardArrowDownIcon sx={{ width: '20px', height: '20px' }} />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default SkillsTemplate;
